import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import AdminLangBar from "./AdminLangBar";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";
import RichTextEditor from "../../components/RichTextEditor";
import ImageUploader from "../../components/ImageUploader";

const EMPTY = {
  slug: "",
  title: "",
  excerpt: "",
  date: new Date().toISOString().slice(0, 10),
  readTime: "5 min read",
  image: "",
  body: "",
  seo_title: "",
  meta_description: "",
};

const slugify = (s) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const inputCls =
  "w-full bg-transparent border-b border-[#E5DFD5] py-2.5 text-sm focus:outline-none focus:border-[#C5A880]";

export default function AdminBlogEdit({ mode }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const isNew = mode === "new";
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [lang, setLang] = useState("en");
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/blog/${slug}`, { params: { lang } });
        setForm({ ...EMPTY, ...data });
      } catch {
        toast.error("Failed to load");
        navigate("/admin/blog");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, isNew, navigate, lang, reloadTick]);

  const update = (k) => (e) =>
    setForm({ ...form, [k]: e.target ? e.target.value : e });

  const save = async (e) => {
    e.preventDefault();
    if (!form.title) {
      toast.error("Title required");
      return;
    }
    const payload = { ...form, slug: form.slug || slugify(form.title) };
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/admin/blog", payload);
        toast.success("Post created");
        navigate("/admin/blog");
      } else {
        await api.put(`/admin/blog/${slug}`, payload, { params: { lang } });
        toast.success(lang === "en" ? "Saved" : `Saved (${lang.toUpperCase()} translation)`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <div className="p-10">
        <Loader2 className="w-6 h-6 animate-spin text-[#C5A880]" strokeWidth={1.5} />
      </div>
    );

  return (
    <div className="p-10 lg:p-16 max-w-[1000px]" data-testid="admin-blog-edit">
      <Link
        to="/admin/blog"
        className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#73706B] hover:text-[#1A1A1A] mb-8"
      >
        <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> All Posts
      </Link>
      <h1
        className="text-4xl lg:text-5xl font-light mb-12"
        style={{ fontFamily: "'Cormorant Garamond', serif" }}
      >
        {isNew ? "New Blog Post" : form.title}
      </h1>

      <AdminLangBar lang={lang} onChange={setLang} kind="blog" docKey={slug} disabled={isNew} onTranslated={() => setReloadTick((n) => n + 1)} />

      <form onSubmit={save} className="space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="text-[10px] tracking-[0.25em] uppercase text-[#73706B] mb-2 block">
              Title *
            </label>
            <input
              required
              value={form.title}
              onChange={update("title")}
              data-testid="post-field-title"
              className={inputCls}
            />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.25em] uppercase text-[#73706B] mb-2 block">
              Slug
            </label>
            <input
              value={form.slug}
              onChange={update("slug")}
              placeholder={slugify(form.title)}
              data-testid="post-field-slug"
              className={inputCls}
            />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.25em] uppercase text-[#73706B] mb-2 block">
              Date
            </label>
            <input value={form.date} onChange={update("date")} className={inputCls} />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.25em] uppercase text-[#73706B] mb-2 block">
              Read time
            </label>
            <input value={form.readTime} onChange={update("readTime")} className={inputCls} />
          </div>
        </div>

        <ImageUploader
          label="Featured image"
          value={form.image}
          onChange={(v) => setForm({ ...form, image: v })}
          testId="post-image"
        />

        <div>
          <label className="text-[10px] tracking-[0.25em] uppercase text-[#73706B] mb-2 block">
            Excerpt
          </label>
          <textarea
            value={form.excerpt}
            onChange={update("excerpt")}
            rows={3}
            data-testid="post-field-excerpt"
            className={`${inputCls} resize-none`}
          />
        </div>

        <div>
          <label className="text-[10px] tracking-[0.25em] uppercase text-[#73706B] mb-2 block">
            Body
          </label>
          <RichTextEditor value={form.body} onChange={(v) => setForm({ ...form, body: v })} />
        </div>

        <section className="border-t border-[#E5DFD5] pt-8 space-y-6" data-testid="post-seo-section">
          <div>
            <h2 className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880]">— SEO (optional)</h2>
            <p className="text-xs text-[#73706B] mt-2">
              Leave blank to fall back to the post title and excerpt.
            </p>
          </div>
          <div>
            <label className="text-[10px] tracking-[0.25em] uppercase text-[#73706B] mb-2 block">
              SEO title (browser tab &amp; Google result)
            </label>
            <input
              value={form.seo_title || ""}
              onChange={update("seo_title")}
              placeholder={form.title ? `${form.title} | Finca Cortesin New Developments` : "Custom SEO title for this post"}
              data-testid="post-field-seo-title"
              className={inputCls}
              maxLength={70}
            />
            <p className="text-[10px] text-[#9A968D] mt-1">
              {(form.seo_title || "").length}/70 — Google typically shows the first 60 characters.
            </p>
          </div>
          <div>
            <label className="text-[10px] tracking-[0.25em] uppercase text-[#73706B] mb-2 block">
              Meta description (Google snippet)
            </label>
            <textarea
              value={form.meta_description || ""}
              onChange={update("meta_description")}
              rows={3}
              placeholder={form.excerpt || "Concise 1-2 sentence description that appears under the title in search results."}
              data-testid="post-field-meta-description"
              className={`${inputCls} resize-none`}
              maxLength={170}
            />
            <p className="text-[10px] text-[#9A968D] mt-1">
              {(form.meta_description || "").length}/170 — Google typically shows the first 155-160 characters.
            </p>
          </div>
        </section>

        <div className="flex items-center gap-4 border-t border-[#E5DFD5] pt-8">
          <button
            type="submit"
            disabled={saving}
            data-testid="post-save"
            className="bg-[#1A1A1A] text-[#F9F6F0] px-8 py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-[#C5A880] disabled:opacity-60 transition-colors inline-flex items-center gap-2"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />}
            {saving ? "Saving" : isNew ? "Create Post" : "Save Changes"}
          </button>
          <Link
            to="/admin/blog"
            className="text-[11px] tracking-[0.25em] uppercase text-[#73706B] hover:text-[#1A1A1A]"
          >
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
